'use client';

import moment, { Moment } from "moment"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import * as echarts from 'echarts'

import { categoricalColorSchemes } from "@nivo/colors"
import { useDarkMode } from "@/hooks/useDarkMode";
import { useIsMobile } from "@/hooks/isMobile";
import { Button } from "../ui/button";
import { TrimContext } from "@/contexts/trimContext";

export type ColorSchemeKey = keyof typeof categoricalColorSchemes;

export type YAxisPreset = 'currency' | 'percent' | 'number' | 'bytes' | 'duration' | 'exponential';

export interface YAxisConfig {
  preset?: YAxisPreset;
  name?: string;
  position?: 'left' | 'right';
  formatter?: (value: number) => string;
  max?: number;
  min?: number;
  scale?: boolean;
  show?: boolean;
  inverse?: boolean;
  offset?: number;
}

export interface DatasetSource {
  source: (Date | number | string)[][];
  dimensions?: string[];
}

export interface DatasetSeriesConfig {
  name?: string;
  type?: 'line';
  color?: string;
  yAxisIndex?: number;
}

// Old API props (backward compatible)
interface OldYAxisProps {
  customAxes?: 'no';
  yAxisFormatter?: (value: number) => string;
  yAxisMax?: number;
  yAxisScale?: boolean;
}

// New API props (preset-based, requires at least one y-axis)
interface NewYAxisProps {
  customAxes: 'yes';
  yAxes: readonly [YAxisConfig, ...YAxisConfig[]];
}

export interface TooltipConfig {
  xAxisFormatter?: (value: Date) => string;
  seriesNameFormatter?: (name: string) => string;
  seriesValueFormatter?: (value: number) => string;
}

export interface LegendAction {
  name: string,
  icon: any,
  action: () => void
}

// Base props (shared between old and new API)
interface BaseDatasetLineChartProps {
  // Required
  dataset: DatasetSource;

  // Optional configuration
  title?: string;
  description?: string;
  height?: string;
  showLegend?: boolean;
  connectNulls?: boolean;
  smooth?: boolean;
  
  // Tooltip configuration
  tooltip?: TooltipConfig;
  
  // Styling
  colorScheme?: ColorSchemeKey;
  stack?: boolean;
  stackOrder?: 'ascending' | 'descending' | 'insideOut' | 'none' | 'seriesDesc' | 'seriesAsc';
  symbol?: 'circle' | 'emptyCircle' | 'rect' | 'roundRect' | 'triangle' | 'diamond' | 'pin' | 'arrow' | 'none';
  
  // Series configuration (optional - will be auto-generated if not provided)
  series?: DatasetSeriesConfig[];

  trim?: Moment
  showTotal?: boolean

  legendActions?: LegendAction[]
}

// Union type for mutually exclusive old and new API
export type DatasetLineChartProps = BaseDatasetLineChartProps & (OldYAxisProps | NewYAxisProps);

const defaultProps: Partial<DatasetLineChartProps> = {
  height: 'h-full',
  showLegend: true,
  yAxisScale: true,
  colorScheme: 'tableau10',
  stack: true,
  stackOrder: 'seriesDesc',
  symbol: 'none',
  connectNulls: false,
  smooth: false,
  legendActions: []
} as const;

const defaultValueFormatter: (n: number) => string = (n: number) => {
  if (n > 1) {
    return n.toString()
  } else {
    return n.toExponential()
  }
}

// Preset formatters for common y-axis types
const presetFormatters: Record<YAxisPreset, (value: number) => string> = {
  currency: (value: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value),
  
  percent: (value: number) => new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  }).format(value),
  
  number: (value: number) => new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value),
  
  bytes: (value: number) => {
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let unitIndex = 0;
    let size = value;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(2)} ${units[unitIndex]}`;
  },
  
  duration: (value: number) => {
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    const seconds = Math.floor(value % 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  },
  
  exponential: (value: number) => value.toExponential(2),
};

// Get formatter from config (preset or custom)
const getYAxisFormatter = (config: YAxisConfig): ((value: number) => string) | undefined => {
  if (config.formatter) {
    return config.formatter;
  }
  if (config.preset) {
    return presetFormatters[config.preset];
  }
  return undefined;
};

// Convert props to yAxes array (handles both old and new API)
const normalizeYAxes = (
  props: DatasetLineChartProps
): YAxisConfig[] => {
  // If yAxes is provided (new API), use it
  if ('yes' == props.customAxes) {
    return props.yAxes as any;
  } else {
    // Otherwise, use backward compatible props (old API)
    const config: YAxisConfig = {
      position: 'left',
      scale: props.yAxisScale,
      max: props.yAxisMax,
    };

    if (props.yAxisFormatter) {
      config.formatter = props.yAxisFormatter;
    }
    return [config];
  }
};

// Extract series names from dataset
const extractSeriesNames = (dataset: DatasetSource): string[] => {
  let notSeries = ['day', 'minute', 'hour', 'month', 'date', 'block_date', 'date_unix']
  // Method 1: Use dimensions if provided
  if (dataset.dimensions) {
    return dataset.dimensions.slice(1).filter(k => !notSeries.includes(k)); // Skip first column (x-axis)
  }
  
  // Method 2: Use first row as header if it contains strings
  const firstRow = dataset.source[0];
  if (firstRow && firstRow.every(cell => typeof cell === 'string')) {
    return (firstRow.slice(1) as string[]).filter(k => !notSeries.includes(k));
  }
  
  // Method 3: Auto-generate from column indices
  const columnCount = dataset.source[0]?.length - 1 || 0;
  return Array.from({ length: columnCount }, (_, i) => String(i + 1)).filter(k => !notSeries.includes(k));
};

export function DatasetLineChart(props: DatasetLineChartProps) {
  const {
    dataset,
    title,
    description,
    height = defaultProps.height,
    showLegend = defaultProps.showLegend,
    tooltip,
    colorScheme = defaultProps.colorScheme,
    stack = defaultProps.stack,
    stackOrder = defaultProps.stackOrder,
    symbol = defaultProps.symbol,
    connectNulls = defaultProps.connectNulls,
    smooth = defaultProps.smooth,
    series: seriesConfig,
    showTotal,
    legendActions = defaultProps.legendActions
  } = props;
  const [hovered, setHovered] = useState<string | undefined>(undefined)
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())
  const trim = useContext(TrimContext)
  const [chartInstance, setChartInstance] = useState<echarts.ECharts>()
  const chartRef = useRef<HTMLDivElement>(null)
  const isDark = useDarkMode()
  const isMobile = useIsMobile()
 
  const colors = categoricalColorSchemes[colorScheme || 'tableau10'];
 
  // Extract series names from dataset
  const seriesNames = useMemo(() => {
    return extractSeriesNames(dataset);
  }, [dataset]);

  const defaultTooltipFormatter = (
    params: any[]
  ) => {
    const date = params[0].axisValue;
    const xAxisFormatter = tooltip?.xAxisFormatter || ((v: Date) => moment.utc(v).format('YYYY-MM-DD'))
    let result = `${xAxisFormatter(date)}<br/>`;
    let resultGrid = `<div class='grid grid-cols-2 auto-cols-fr text-xs'>`
    resultGrid += `<div class='col-span-2'><span>${xAxisFormatter(date)}</span></div>`
    
    for (let param of params) {
      const value = param.value[param.seriesIndex+1] as number;
      if (Number.isNaN(value) || value === 0 || !value) {
        continue
      }
      const isHovered = param.seriesName === hovered;
      const style = isHovered ? 'font-weight: bold;' : '';
      const classes = isHovered? 'font-bold text-sm': '';
      
      const formattedName = tooltip?.seriesNameFormatter 
        ? tooltip.seriesNameFormatter(param.seriesName)
        : param.seriesName;
      const formattedValue = tooltip?.seriesValueFormatter
        ? tooltip.seriesValueFormatter(value)
        : defaultValueFormatter(value);
        
      
      result += `<span style="margin-right: 3em; ${style}">${param.marker} ${formattedName}</span><strong style="float:right; ${style}">${formattedValue}</strong><br/>`;
      resultGrid += `<div class="${classes}">${param.marker} ${formattedName}</div><div class='text-right ${classes}''>${formattedValue}</div>`
    }
    if (showTotal) {
        const to_add = params[0].dimensionNames.slice(1).map((d: string, idx: number) => {
          if (hiddenSeries.has(d)) return 0
          else return params[0].value.slice(1)[idx]
        })
        const total = to_add.reduce((acc: number, v?: string) => acc + (Number(v) || 0), 0) as number
        const formattedTotal = tooltip?.seriesValueFormatter
            ? tooltip.seriesValueFormatter(total)
            : defaultValueFormatter(total);
        result += `<span style="margin-right: 3em; font-weight: bold">Total</span><strong style="float:right; font-weight: bold">${formattedTotal}</strong><br/>`;
        resultGrid += `<div class="font-bold">Total</div><div class='font-bold text-right'>${formattedTotal}</div>`
    }
    
    return resultGrid;
  };

  useEffect(() => {
    const element = chartRef.current;
    if (!element) return;

    // Ensure element has dimensions before initializing
    const initChart = () => {

      const _chartInstance = echarts.init(element);
      setChartInstance(_chartInstance);

      // Handle window resize
      const handleResize = () => {
        _chartInstance.resize();
      };

      // Handle container resize using ResizeObserver
      const resizeObserver = new ResizeObserver(() => {
        _chartInstance.resize();
      });
      resizeObserver.observe(element);

      // Handle series hover
      const handleMouseOver = (params: any) => {
        if (params.componentType === 'series') {
          setHovered(params.seriesName);
        }
      };

      const handleMouseOut = () => {
        setHovered(undefined);
      };

      window.addEventListener('resize', handleResize);
      _chartInstance.on('mouseover', handleMouseOver);
      _chartInstance.on('globalout', handleMouseOut);

      return () => {
        window.removeEventListener('resize', handleResize);
        resizeObserver.disconnect();
        _chartInstance.off('mouseover', handleMouseOver);
        _chartInstance.off('globalout', handleMouseOut);
        _chartInstance.dispose();
      };
    };

    // Start initialization
    const cleanup = initChart();

    return () => {
      cleanup?.();
    };
  }, [])

  useEffect(() => {
    if (!seriesNames || seriesNames.length === 0) return;
    const textColor = isDark?"#ccc":"#222"

    // Normalize y-axes from props (handles both old and new API)
    const normalizedYAxes = normalizeYAxes(props);

    // Build y-axis configurations from normalized configs
    const yAxisConfigs = normalizedYAxes.map((config, index) => {
      const formatter = getYAxisFormatter(config);
      const axisConfig: any = {
        name: config.name || `y${index}`,
        type: 'value',
        scale: trim?true:config.scale,
        position: config.position || (index === 0 ? 'left' : 'right'),
        axisLabel: {
          formatter: formatter ? (n: any) => formatter(n) : undefined,
          color: textColor
        },
        axisLine: {
          lineStyle: {
            color: textColor
          }
        },
        axisTick: {
          lineStyle: {
            color: textColor
          }
        }
      };

      // Apply optional properties
      if (config.max !== undefined) {
        axisConfig.max = config.max;
      }
      if (config.min !== undefined) {
        axisConfig.min = config.min;
      }
      if (config.show !== undefined) {
        axisConfig.show = config.show;
      }
      if (config.inverse !== undefined) {
        axisConfig.inverse = config.inverse;
      }
      if (config.offset !== undefined) {
        axisConfig.offset = config.offset;
      }

      // Add tooltip formatter if needed
      if (formatter) {
        axisConfig.tooltip = {
          formatter: (v: number) => formatter(v),
        };
      }

      return axisConfig;
    });

    const breaks = trim?[{
      start: 0,
      end: trim
    }]:undefined

    const dataZoom = [{
      type: 'slider',
      show: false,
      xAxisIndex: [0],
      startValue: trim??undefined,
      filterMode: 'none'
    }]

    // Build series configuration
    const series = seriesNames.map((name, idx) => {
      const config = seriesConfig?.find(c => c.name === name) || {};
      return {
        type: 'line',
        name: name,
        lineStyle: {
          opacity: (hovered ? (hovered === (name) ? 1 : 0.5) : 1)
        },
        yAxisIndex: config.yAxisIndex || 0,
        data: hiddenSeries.has(name)?[]:undefined,
        connectNulls: connectNulls,
        smooth: smooth,
        stack: stack ? 'total' : undefined,
        stackOrder: stack ? stackOrder : undefined,
        symbol: symbol,
        color: config.color || colors[idx % colors.length],
        triggerLineEvent: true,
        triggerAreaEvent: true,
        encode: {
          x: 0, // First column is x-axis
          y: idx + 1 // Series data is in column idx + 1
        }
      };
    });

    const option = {
      darkMode: isDark,
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '3%',
        containLabel: true
      },
      dataset: {
        source: dataset.source
      },
      dataZoom,
      xAxis: {
        type: 'time',
        name: 'Date',
        //breaks,
        axisLabel: {
          color: textColor
        },
        axisLine: {
          lineStyle: {color: textColor}
        },
        axisTick: {
          lineStyle: {color: textColor}
        }
      },
      yAxis: yAxisConfigs,
      series,
      tooltip: {
        trigger: 'axis',
        formatter: defaultTooltipFormatter,
        className: 'text-fg bg-background xs:text-xs',
        backgroundColor: '',
        textStyle: {
          color: '',
        },
      },
      legend: {
        show: false
      }
    }

    chartInstance?.setOption(option)
  }, [isDark, chartInstance, dataset, hovered, title, props, tooltip, stack, stackOrder, symbol, colors, trim, seriesNames, seriesConfig, hiddenSeries])

  const handleLegendClick = (name: string) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    setHiddenSeries(prev => {
      // If all series are hidden, show all; otherwise hide all
      if (prev.size === seriesNames.length) {
        return new Set();
      } else {
        return new Set(seriesNames);
      }
    });
  };

  const textColor = isDark?"#ccc":"#222"

  return (
    <div className='flex flex-col h-full lg:flex-row'>
      <div className='flex flex-col grow'> 
        { /* title */ }
        <div className="flex flex-row">
          <span className="block text-2xl lg:ml-8 ml-4 grow">{title}</span>
          <span className="block text-xs place-self-end">{description}</span>
        </div>
        { /* chart */}
        <div className='w-full h-full' ref={chartRef} />
      </div>
      {/* legend */}
      {showLegend && (
        <div className='flex flex-col'>
        
          <div className={`flex hover:bg-primary/10 bg-muted rounded ${isMobile ? 'flex-row w-full' : 'flex-col w-[10vw] h-full ml-4'} gap-2 p-2 overflow-scroll`}>
          <button
            onClick={handleToggleAll}
            className={`flex items-center gap-2 px-2 py-1 rounded text-sm transition-opacity font-medium ${
              hiddenSeries.size === seriesNames.length ? 'opacity-100' : 'opacity-100'
            }`}
            style={{ color: textColor }}
          >
            <span className="truncate">
              {hiddenSeries.size === seriesNames.length ? 'Show All' : 'Hide All'}
            </span>
          </button>
          {legendActions?.map(action => <button
            onClick={action.action}
            className={`flex items-center gap-2 px-2 py-1 rounded text-sm font-medium`}
            style={{ color: textColor }}
            key={action.name}
          >
            <action.icon size='14' />
            <span className="truncate">
              {action.name}
            </span>
          </button>)}
            {seriesNames.map((name, idx) => {
            const config = seriesConfig?.find(c => c.name === name) || {};
            const displayName = config.name || name;
            const isHidden = hiddenSeries.has(displayName);
            return (
              <button
                key={displayName}
                onClick={() => handleLegendClick(displayName)}
                onMouseEnter={() => setHovered(displayName)}
                onMouseLeave={() => setHovered(undefined)}
                className={`flex items-center gap-2 px-2 py-1 rounded text-sm transition-opacity ${
                  isHidden ? 'opacity-40' : 'opacity-100'
                } ${hovered === displayName ? 'bg-primary/10' : ''}`}
                style={{ color: textColor }}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: config.color || colors[idx % colors.length] }}
                />
                <span className="truncate">{displayName.trim()}</span>
              </button>
            );
          })}
          </div>
      </div>)}

    </div>)
}
